import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useToast } from '../components/ui/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.qrmenu.rw/api';

interface Table {
    id: number;
    name: string;
    capacity: number;
    location: string;
    available_slots: string[];
    booking_fee: number;
    is_booked?: boolean;
}

interface BookingSettings {
    booking_enabled: boolean;
    booking_fee: number;
    slot_duration_minutes: number;
    booking_mode: 'restaurant' | 'bar';
}

const BookingPage: React.FC = () => {
    const { establishmentId } = useParams<{ establishmentId: string }>();
    const history = useHistory();
    const { t } = useTranslation();
    const { showToast } = useToast();

    const [loading, setLoading] = useState(true);
    const [tables, setTables] = useState<Table[]>([]);
    const [settings, setSettings] = useState<BookingSettings | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [selectedTable, setSelectedTable] = useState<Table | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<string>('');
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [step, setStep] = useState<'select' | 'details' | 'payment' | 'success'>('select');
    const [transactionRef, setTransactionRef] = useState<string>('');
    const [bookingCode, setBookingCode] = useState<string>('');
    const [checkingPayment, setCheckingPayment] = useState(false);
    const [establishmentName, setEstablishmentName] = useState('');

    useEffect(() => {
        fetchSettings();
        fetchEstablishmentInfo();
    }, [establishmentId]);

    useEffect(() => {
        if (settings?.booking_enabled) {
            fetchAvailableTables();
        }
    }, [selectedDate, settings]);

    const fetchEstablishmentInfo = async () => {
        try {
            const response = await axios.get(`${API_URL}/public/establishments/${establishmentId}`);
            setEstablishmentName(response.data.name);
            if (response.data.name) {
                document.title = `${response.data.name} — Book a Table`;
            }
            document.body.dataset.theme = response.data.client_theme || 'royal-dark';
        } catch (error) {
            console.error('Error fetching establishment:', error);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API_URL}/bookings/public/${establishmentId}/settings`);
            setSettings({
                ...response.data,
                booking_mode: response.data.booking_mode || 'bar'
            });
            if (!response.data.booking_enabled) {
                showToast('Booking is not available at this establishment', 'warning');
                history.push('/');
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            history.push('/');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableTables = async () => {
        try {
            const response = await axios.get(
                `${API_URL}/bookings/public/${establishmentId}/available-tables?date=${selectedDate}`
            );
            setTables(response.data.tables || []);
        } catch (error) {
            console.error('Error fetching tables:', error);
        }
    };

    const handleSelectTable = (table: Table) => {
        setSelectedTable(table);
        setSelectedSlot('');
    };

    const handleSelectSlot = (slot: string) => {
        setSelectedSlot(slot);
        setStep('details');
    };

    const handleInitiateBooking = async () => {
        if (!customerName.trim() || !customerPhone.trim() || !customerEmail.trim()) {
            showToast('Please fill in all required fields', 'warning');
            return;
        }

        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(customerEmail)) {
            showToast('Please enter a valid email address', 'warning');
            return;
        }

        // Validate phone number (Rwandan format)
        const phoneRegex = /^(07[2-9]|078|079)\d{7}$/;
        const cleanPhone = customerPhone.replace(/\D/g, '');
        if (!phoneRegex.test(cleanPhone)) {
            showToast('Please enter a valid Rwandan phone number', 'warning');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/bookings/public/initiate`, {
                establishment_id: parseInt(establishmentId),
                table_id: selectedTable ? selectedTable.id : 0,
                booking_date: selectedDate,
                time_slot: selectedSlot,
                customer_name: customerName,
                customer_phone: cleanPhone,
                customer_email: customerEmail
            });

            setTransactionRef(response.data.transaction_ref);
            setStep('payment');
            showToast('Payment request sent to your phone. Please approve.', 'success');

            // Start polling for payment status
            pollPaymentStatus(response.data.transaction_ref);
        } catch (error) {
            showToast((error && error.response && error.response.data && error.response.data.error) || 'Failed to initiate booking', 'error');
        } finally {
            setLoading(false);
        }
    };

    const pollPaymentStatus = async (ref: string) => {
        setCheckingPayment(true);
        let attempts = 0;
        const maxAttempts = 60; // 5 minutes with 5-second intervals

        const checkStatus = async () => {
            try {
                const response = await axios.get(`${API_URL}/bookings/public/status/${ref}`);

                if (response.data.status === 'confirmed') {
                    setBookingCode(response.data.booking_code);
                    setStep('success');
                    setCheckingPayment(false);
                    showToast('Booking confirmed!', 'success');
                    return;
                }

                if (response.data.status === 'failed') {
                    showToast('Payment failed. Please try again.', 'error');
                    setStep('details');
                    setCheckingPayment(false);
                    return;
                }

                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(checkStatus, 5000);
                } else {
                    showToast('Payment verification timed out. Please check your booking status.', 'warning');
                    setCheckingPayment(false);
                }
            } catch (error) {
                console.error('Error checking payment status:', error);
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(checkStatus, 5000);
                }
            }
        };

        checkStatus();
    };

    const formatTime = (time: string): string => {
        const [hours, minutes] = time.split(':');
        const h = parseInt(hours);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    const getMinDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const getMaxDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 14); // Allow booking up to 14 days ahead
        return date.toISOString().split('T')[0];
    };

    if (loading && !settings) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
                color: 'white'
            }}>
                <div style={{
                    padding: '2rem',
                    borderRadius: '1rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)'
                }}>
                    {t('common.loading')}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
            padding: '2rem 1rem',
            color: 'white'
        }}>
            <LanguageSwitcher />

            {/* Header */}
            <div style={{
                textAlign: 'center',
                marginBottom: '2rem',
                paddingTop: '2rem'
            }}>
                <button
                    onClick={() => history.push('/')}
                    style={{
                        position: 'absolute',
                        top: '1rem',
                        left: '1rem',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: 'none',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        cursor: 'pointer'
                    }}
                >
                    ← Back
                </button>

                <h1 style={{
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '0.5rem'
                }}>
                    Book a Table
                </h1>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.25rem' }}>
                    {establishmentName}
                </p>
            </div>

            <div style={{
                maxWidth: '600px',
                margin: '0 auto',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '1.5rem',
                padding: '2rem'
            }}>
                {/* Progress Steps */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    marginBottom: '2rem'
                }}>
                    {['select', 'details', 'payment', 'success'].map((s, i) => (
                        <div
                            key={s}
                            style={{
                                width: '60px',
                                height: '4px',
                                background: ['select', 'details', 'payment', 'success'].indexOf(step) >= i
                                    ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                                    : 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '2px'
                            }}
                        />
                    ))}
                </div>

                {/* Step 1: Select Table & Time */}
                {step === 'select' && (
                    <>
                        {/* Date Picker */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                                Select Date
                            </label>
                            <input
                                type="date"
                                value={selectedDate}
                                min={getMinDate()}
                                max={getMaxDate()}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '1rem',
                                    border: '2px solid rgba(255, 215, 0, 0.3)',
                                    borderRadius: '0.75rem',
                                    background: 'rgba(0, 0, 0, 0.3)',
                                    color: 'white',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        {/* Tables List */}
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                                {settings?.booking_mode === 'bar' ? 'Select Your Table' : 'Available Tables'}
                            </label>
                            {tables.length === 0 ? (
                                <p style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', padding: '2rem' }}>
                                    No tables available for booking on this date
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {tables.map(table => {
                                        const isBooked = table.is_booked || table.available_slots.length === 0;
                                        return (
                                            <div
                                                key={table.id}
                                                onClick={() => !isBooked && handleSelectTable(table)}
                                                style={{
                                                    padding: '1rem',
                                                    border: selectedTable?.id === table.id
                                                        ? '2px solid #FFD700'
                                                        : isBooked
                                                            ? '2px solid rgba(255, 107, 107, 0.3)'
                                                            : '2px solid rgba(255, 255, 255, 0.1)',
                                                    borderRadius: '0.75rem',
                                                    cursor: isBooked ? 'not-allowed' : 'pointer',
                                                    background: selectedTable?.id === table.id
                                                        ? 'rgba(255, 215, 0, 0.1)'
                                                        : isBooked
                                                            ? 'rgba(255, 107, 107, 0.05)'
                                                            : 'transparent',
                                                    opacity: isBooked ? 0.6 : 1,
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        <strong>{table.name}</strong>
                                                        <span style={{ marginLeft: '1rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                                                            {table.capacity} seats
                                                        </span>
                                                        {isBooked && (
                                                            <span style={{
                                                                marginLeft: '0.5rem',
                                                                background: '#ff6b6b',
                                                                color: 'white',
                                                                padding: '0.15rem 0.5rem',
                                                                borderRadius: '0.25rem',
                                                                fontSize: '0.7rem',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                BOOKED
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <span style={{ color: isBooked ? 'rgba(255, 255, 255, 0.3)' : '#FFD700', fontWeight: 'bold', display: 'block' }}>
                                                            {table.booking_fee?.toLocaleString()} RWF
                                                        </span>
                                                        <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>{table.location}</span>
                                                    </div>
                                                </div>

                                                {selectedTable?.id === table.id && !isBooked && table.available_slots.length > 0 && (
                                                    <div style={{ marginTop: '1rem' }}>
                                                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                                                            {settings?.booking_mode === 'bar'
                                                                ? 'Select your arrival time:'
                                                                : 'Select a time slot:'}
                                                        </p>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                            {table.available_slots.map(slot => (
                                                                <button
                                                                    key={slot}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleSelectSlot(slot);
                                                                    }}
                                                                    style={{
                                                                        padding: '0.5rem 1rem',
                                                                        background: selectedSlot === slot
                                                                            ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                                                                            : 'rgba(255, 255, 255, 0.1)',
                                                                        border: 'none',
                                                                        borderRadius: '0.5rem',
                                                                        color: selectedSlot === slot ? '#0a0a0a' : 'white',
                                                                        cursor: 'pointer',
                                                                        fontSize: '0.9rem',
                                                                        fontWeight: selectedSlot === slot ? 'bold' : 'normal'
                                                                    }}
                                                                >
                                                                    {formatTime(slot)}
                                                                </button>
                                                            ))}
                                                        </div>
                                                        {settings?.booking_mode === 'bar' && (
                                                            <p style={{ color: 'rgba(255, 215, 0, 0.7)', marginTop: '0.75rem', fontSize: '0.8rem' }}>
                                                                ✨ Table is yours for the evening once you arrive
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Step 2: Customer Details */}
                {step === 'details' && (
                    <>
                        <div style={{ marginBottom: '1.5rem', textAlign: 'center', padding: '1rem', background: 'rgba(255, 215, 0, 0.1)', borderRadius: '0.75rem' }}>
                            <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                <strong>{selectedTable?.name}</strong> • {settings?.booking_mode === 'bar' ? 'Arriving' : ''} {formatTime(selectedSlot)} • {selectedDate}
                            </p>
                            {settings?.booking_mode === 'bar' && (
                                <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                                    Reserved for your evening
                                </p>
                            )}
                            <p style={{ color: '#FFD700', marginTop: '0.5rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
                                Booking Fee: {(selectedTable?.booking_fee ?? settings?.booking_fee ?? 0).toLocaleString()} RWF
                            </p>
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                                Your Name
                            </label>
                            <input
                                type="text"
                                value={customerName}
                                onChange={(e) => setCustomerName(e.target.value)}
                                placeholder="Enter your name"
                                style={{
                                    width: '100%',
                                    padding: '1rem',
                                    border: '2px solid rgba(255, 215, 0, 0.3)',
                                    borderRadius: '0.75rem',
                                    background: 'rgba(0, 0, 0, 0.3)',
                                    color: 'white',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                                Phone Number (MTN or Airtel)
                            </label>
                            <input
                                type="tel"
                                value={customerPhone}
                                onChange={(e) => setCustomerPhone(e.target.value)}
                                placeholder="07XXXXXXXX"
                                style={{
                                    width: '100%',
                                    padding: '1rem',
                                    border: '2px solid rgba(255, 215, 0, 0.3)',
                                    borderRadius: '0.75rem',
                                    background: 'rgba(0, 0, 0, 0.3)',
                                    color: 'white',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={customerEmail}
                                onChange={(e) => setCustomerEmail(e.target.value)}
                                placeholder="your@email.com"
                                style={{
                                    width: '100%',
                                    padding: '1rem',
                                    border: '2px solid rgba(255, 215, 0, 0.3)',
                                    borderRadius: '0.75rem',
                                    background: 'rgba(0, 0, 0, 0.3)',
                                    color: 'white',
                                    fontSize: '1rem'
                                }}
                            />
                            <small style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>Confirmation code will be sent here</small>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <button
                                onClick={() => setStep('select')}
                                style={{
                                    flex: 1,
                                    padding: '1rem',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    border: 'none',
                                    borderRadius: '0.75rem',
                                    color: 'white',
                                    fontSize: '1rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Back
                            </button>
                            <button
                                onClick={handleInitiateBooking}
                                disabled={loading}
                                style={{
                                    flex: 2,
                                    padding: '1rem',
                                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                    border: 'none',
                                    borderRadius: '0.75rem',
                                    color: '#0a0a0a',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.7 : 1
                                }}
                            >
                                {loading ? 'Processing...' : `Pay ${(selectedTable?.booking_fee ?? settings?.booking_fee ?? 0).toLocaleString()} RWF`}
                            </button>
                        </div>
                    </>
                )}

                {/* Step 3: Payment Pending */}
                {step === 'payment' && (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            margin: '0 auto 1.5rem',
                            border: '4px solid rgba(255, 215, 0, 0.3)',
                            borderTopColor: '#FFD700',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }} />
                        <style>
                            {`@keyframes spin { to { transform: rotate(360deg); } }`}
                        </style>

                        <h2 style={{ marginBottom: '1rem', color: '#FFD700' }}>
                            Waiting for Payment
                        </h2>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1rem' }}>
                            Please approve the payment request on your phone
                        </p>
                        <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.9rem' }}>
                            Payment of {(selectedTable?.booking_fee ?? settings?.booking_fee ?? 0).toLocaleString()} RWF
                        </p>
                    </div>
                )}

                {/* Step 4: Success */}
                {step === 'success' && (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            margin: '0 auto 1.5rem',
                            background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="white" style={{ width: '40px', height: '40px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                            </svg>
                        </div>

                        <h2 style={{ marginBottom: '1rem', color: '#4CAF50' }}>
                            Booking Confirmed!
                        </h2>

                        <div style={{
                            background: 'rgba(255, 215, 0, 0.1)',
                            border: '2px solid #FFD700',
                            borderRadius: '1rem',
                            padding: '1.5rem',
                            marginBottom: '1.5rem'
                        }}>
                            <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '0.5rem' }}>
                                Your Booking Code
                            </p>
                            <p style={{
                                fontSize: '2.5rem',
                                fontWeight: 'bold',
                                color: '#FFD700',
                                letterSpacing: '0.25em'
                            }}>
                                {bookingCode}
                            </p>
                        </div>

                        <div style={{ marginBottom: '1.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            <p><strong>{selectedTable?.name}</strong></p>
                            <p>{formatTime(selectedSlot)} • {selectedDate}</p>
                        </div>

                        <div style={{
                            background: 'rgba(255, 165, 0, 0.1)',
                            borderRadius: '0.75rem',
                            padding: '1rem',
                            marginBottom: '1.5rem'
                        }}>
                            <p style={{ color: '#FFA500', fontSize: '0.9rem' }}>
                                Save this code! You'll need to enter it when you scan the table's QR code.
                            </p>
                        </div>

                        <button
                            onClick={() => history.push('/')}
                            style={{
                                padding: '1rem 2rem',
                                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                border: 'none',
                                borderRadius: '0.75rem',
                                color: '#0a0a0a',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookingPage;
